/**
 * Maintenance Requests API
 * RTK Query endpoints for maintenance request management
 */

import { baseApi } from "./baseApi";

/**
 * Maintenance Priority enum
 */
export type MaintenancePriority = "low" | "medium" | "high" | "urgent";

/**
 * Maintenance Status enum
 */
export type MaintenanceStatus =
	| "pending"
	| "in_progress"
	| "completed"
	| "cancelled"
	| "on_hold";

/**
 * Maintenance Request interface matching backend
 */
export interface MaintenanceRequest {
	id: string;
	organization_id?: string;
	organization_name?: string;
	property_id: string;
	property_name?: string;
	requested_by_id: string;
	requested_by_name?: string;
	requested_by_email?: string;
	requested_by_phone?: string;
	assigned_to_id?: string;
	assigned_to_name?: string;
	title: string;
	description: string;
	priority: MaintenancePriority;
	status: MaintenanceStatus;
	location?: string;
	requested_date: string;
	scheduled_date?: string;
	completed_date?: string;
	estimated_cost?: number;
	actual_cost?: number;
	notes?: string;
	resolution_notes?: string;
	time_spent?: string;
	photo_urls?: string[];
	created_at: string;
	updated_at: string;
}

/**
 * Maintenance request list response interface
 */
export interface MaintenanceRequestListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: MaintenanceRequest[];
}

/**
 * Create/Update maintenance request request interface
 */
export interface MaintenanceRequestData {
	organization_id: string;
	property_id: string;
	requested_by_id: string;
	title: string;
	description: string;
	priority?: MaintenancePriority;
	status?: MaintenanceStatus;
	location?: string;
	scheduled_date?: string;
	estimated_cost?: number;
	notes?: string;
	assigned_to_id?: string;
	time_spent?: number;
}

/**
 * Assign maintenance request data
 */
export interface AssignMaintenanceData {
	technician_id: string;
}

/**
 * Complete maintenance request data
 */
export interface CompleteMaintenanceData {
	resolution_notes?: string;
	actual_cost?: number;
}

/**
 * Upload photo data
 */
export interface UploadPhotoData {
	photo_file: string; // Base64 encoded image
}

/**
 * Maintenance Requests API endpoints
 */
export const maintenanceApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all maintenance requests (with filters, search, pagination)
		 */
		listMaintenanceRequests: builder.query<
			MaintenanceRequestListResponse,
			{
				page?: number;
				page_size?: number;
				status?: MaintenanceStatus;
				priority?: MaintenancePriority;
				property?: string;
				assigned_to?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.status) queryParams.append("status", params.status);
				if (params.priority) queryParams.append("priority", params.priority);
				if (params.property) queryParams.append("property", params.property);
				if (params.assigned_to) queryParams.append("assigned_to", params.assigned_to);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				const queryString = queryParams.toString();
				return {
					url: queryString ? `/maintenance/?${queryString}` : `/maintenance/`,
					method: "GET",
				};
			},
			transformResponse: (response: {
				count: number;
				next: string | null;
				previous: string | null;
				results: any[];
			}) => {
				// Use backend snake_case directly - no transformation needed
				return {
					...response,
					results: response.results?.map((req: any) => ({
						...req,
						// Convert dates to ISO strings if needed
						requested_date: typeof req.requested_date === 'string' ? req.requested_date : new Date(req.requested_date).toISOString(),
						scheduled_date: req.scheduled_date ? (typeof req.scheduled_date === 'string' ? req.scheduled_date : new Date(req.scheduled_date).toISOString()) : undefined,
						completed_date: req.completed_date ? (typeof req.completed_date === 'string' ? req.completed_date : new Date(req.completed_date).toISOString()) : undefined,
						created_at: typeof req.created_at === 'string' ? req.created_at : new Date(req.created_at).toISOString(),
						updated_at: typeof req.updated_at === 'string' ? req.updated_at : new Date(req.updated_at).toISOString(),
					})) || [],
				};
			},
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id }) => ({
								type: "Maintenance" as const,
								id,
							})),
							{ type: "Maintenance", id: "LIST" },
					  ]
					: [{ type: "Maintenance", id: "LIST" }],
		}),

		/**
		 * Get maintenance request by ID
		 */
		getMaintenanceRequest: builder.query<MaintenanceRequest, string>({
			query: (id) => ({
				url: `/maintenance/${id}`,
				method: "GET",
			}),
			transformResponse: (response: any) => {
				// Use backend snake_case directly - no transformation needed
				return {
					...response,
					// Convert dates to ISO strings if needed
					requested_date: typeof response.requested_date === 'string' ? response.requested_date : new Date(response.requested_date).toISOString(),
					scheduled_date: response.scheduled_date ? (typeof response.scheduled_date === 'string' ? response.scheduled_date : new Date(response.scheduled_date).toISOString()) : undefined,
					completed_date: response.completed_date ? (typeof response.completed_date === 'string' ? response.completed_date : new Date(response.completed_date).toISOString()) : undefined,
					created_at: typeof response.created_at === 'string' ? response.created_at : new Date(response.created_at).toISOString(),
					updated_at: typeof response.updated_at === 'string' ? response.updated_at : new Date(response.updated_at).toISOString(),
				};
			},
			providesTags: (result, error, id) => [{ type: "Maintenance", id }],
		}),

		/**
		 * Create new maintenance request
		 */
		createMaintenanceRequest: builder.mutation<
			MaintenanceRequest,
			MaintenanceRequestData
		>({
			query: (data) => ({
				url: "/maintenance/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: [{ type: "Maintenance", id: "LIST" }],
		}),

		/**
		 * Update maintenance request
		 */
		updateMaintenanceRequest: builder.mutation<
			MaintenanceRequest,
			{ id: string; data: Partial<MaintenanceRequestData> }
		>({
			query: ({ id, data }) => ({
				url: `/maintenance/${id}`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Maintenance", id },
				{ type: "Maintenance", id: "LIST" },
			],
		}),

		/**
		 * Delete maintenance request
		 */
		deleteMaintenanceRequest: builder.mutation<void, string>({
			query: (id) => ({
				url: `/maintenance/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: [{ type: "Maintenance", id: "LIST" }],
		}),

		/**
		 * Assign maintenance request to technician
		 */
		assignMaintenanceRequest: builder.mutation<
			MaintenanceRequest,
			{ id: string; data: AssignMaintenanceData }
		>({
			query: ({ id, data }) => ({
				url: `/maintenance/${id}/assign`,
				method: "POST",
				body: {
					technician_id: data.technician_id,
				},
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Maintenance", id },
				{ type: "Maintenance", id: "LIST" },
			],
		}),

		/**
		 * Mark maintenance request as completed
		 */
		completeMaintenanceRequest: builder.mutation<
			MaintenanceRequest,
			{ id: string; data: CompleteMaintenanceData }
		>({
			query: ({ id, data }) => ({
				url: `/maintenance/${id}/complete`,
				method: "POST",
				body: {
					resolution_notes: data.resolution_notes || "",
					actual_cost: data.actual_cost,
				},
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Maintenance", id },
				{ type: "Maintenance", id: "LIST" },
			],
		}),

		/**
		 * Upload photo to maintenance request
		 */
		uploadPhotoToMaintenanceRequest: builder.mutation<
			MaintenanceRequest,
			{ id: string; data: UploadPhotoData }
		>({
			query: ({ id, data }) => ({
				url: `/maintenance/${id}/upload-photo`,
				method: "POST",
				body: {
					photo_file: data.photo_file,
				},
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Maintenance", id },
				{ type: "Maintenance", id: "LIST" },
			],
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListMaintenanceRequestsQuery,
	useGetMaintenanceRequestQuery,
	useCreateMaintenanceRequestMutation,
	useUpdateMaintenanceRequestMutation,
	useDeleteMaintenanceRequestMutation,
	useAssignMaintenanceRequestMutation,
	useCompleteMaintenanceRequestMutation,
	useUploadPhotoToMaintenanceRequestMutation,
} = maintenanceApi;
